import React from "react";
import { Machine } from "../../../game/Machine";
import { ProgressionState } from "../../../game/GameState";
import { SheetShell } from "../../../components/SheetShell";
import { divesToBench } from "../../../game/station-helpers";
import { useShopState } from "../../useShell";
import { useTargeting } from "../useTargeting";
import { ContentsSheet } from "./ContentsSheet";
import { AccessorySheet } from "./AccessorySheet";
import { StatusText } from "./StatusText";

/**
 * The station sheet: the paperwork behind a bench or a container, spread
 * out in the middle of the shop when the player steps up to it (E, or
 * right-clicking the station). Deliberately *not* a modal — the world
 * keeps ticking, the home-screen keys keep working on the station, and
 * walking away folds the sheet back up (TargetingState's tick).
 *
 * A direct-feed machine keeps only its accessory rack (AccessorySheet):
 * a jointer, planer, table saw or miter saw is a switch, a scale or two,
 * and stock you set down, and every one of those is a key on the floor.
 * Fitting a jig is the one thing left that needs a page. Containers list
 * their contents (ContentsSheet).
 *
 * Benches have no card at all — their sheet is the full-window bench
 * view (BenchDiveView), so E at a bench opens no surface here (the
 * open/close state still tracks, so the keys behave).
 *
 * The old component portaled to the body because it rendered from the
 * camera-pinned overlay; here it renders in the HUD root, which is
 * already a fixed whole-window layer — same box, same z ordering. The
 * dimmed shop, the column, and the ✕ off the sheet's corner are the
 * shared SheetShell's; this file draws the sheet.
 */
export function sheetIsBenchView(
  machine: Machine,
  progression: ProgressionState,
): boolean {
  return divesToBench(machine, progression);
}

export const StationSheet: React.FC = () => {
  const { sheetMachine, closeSheet } = useTargeting();
  const gameState = useShopState();

  if (gameState.player.away || gameState.player.carriedMachine != null) {
    return null;
  }

  if (!sheetMachine) {
    return null;
  }

  // The bench view (the full-window work surface) renders elsewhere
  // (BenchDiveView).
  if (sheetIsBenchView(sheetMachine, gameState.progression)) {
    return null;
  }

  return (
    <SheetShell
      onClose={closeSheet}
      closeLabel="Put the sheet away"
      testId="station-sheet"
    >
      <StationSheetBody machine={sheetMachine} />
    </SheetShell>
  );
};

const StationSheetBody: React.FC<{ machine: Machine }> = ({ machine }) => (
  <section className="paper-card space-y-3 shadow-xl">
    <header className="flex items-baseline justify-between border-b-2 border-ink-black/40 pb-1">
      <h3 className="font-condensed font-bold text-lg uppercase tracking-wide">
        {machine.type.name}
      </h3>
      <span className="font-condensed uppercase tracking-[0.15em] text-[0.65rem] text-ink-fade">
        <StatusText machine={machine} />
      </span>
    </header>
    {machine.type.directFeed ? (
      <AccessorySheet machine={machine} />
    ) : (
      <ContentsSheet machine={machine} />
    )}
  </section>
);
